import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SrIcon } from "@/components/ui/SrIcon";
import { NewCustomerDialog } from "@/components/new-project/NewCustomerDialog";
import { cn } from "@/lib/utils";
import { invoiceCustomerGroups, type InvoiceCustomerOption } from "@/lib/invoice-customer";

interface InvoiceCustomerPickerProps<C extends InvoiceCustomerOption> {
  value: C | null;
  mainCustomerId: string | null;
  customers: readonly C[];
  projectInvoices: ReadonlyArray<{ customer_id: string | null }>;
  onChange: (customerId: string) => void;
  /** Kreditnota: kunden er låst til fakturaen som krediteres. */
  lockedReason?: string | null;
}

/**
 * VIB-3030: kunden fakturaen går til. Hovedkunden er valgt fra start; kunder
 * brukt på prosjektet ligger først, og søk dekker hele kundelista.
 */
export function InvoiceCustomerPicker<C extends InvoiceCustomerOption>({
  value,
  mainCustomerId,
  customers,
  projectInvoices,
  onChange,
  lockedReason,
}: InvoiceCustomerPickerProps<C>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const groups = invoiceCustomerGroups({ mainCustomerId, customers, projectInvoices, query });
  const isOther = !!value && value.id !== mainCustomerId;

  const choose = (c: C) => {
    onChange(c.id);
    setOpen(false);
    setQuery("");
  };

  const option = (c: C, hint: string) => {
    const linked = c.poweroffice_customer_id != null;
    return (
      <button
        key={c.id}
        type="button"
        role="option"
        aria-selected={value?.id === c.id}
        onClick={() => choose(c)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-primary/5 focus-visible:outline-none focus-visible:bg-primary/5"
      >
        <span className={cn(!linked && "text-muted-foreground")}>{c.name}</span>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {value?.id === c.id ? "valgt" : linked ? hint : "ikke i PowerOffice"}
        </span>
      </button>
    );
  };

  const group = (label: string, children: React.ReactNode) => (
    <div role="group" aria-label={label}>
      <div className="px-3 pt-2.5 pb-1 text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground">{label}</div>
      {children}
    </div>
  );

  return (
    <div>
      <span id="invoice-customer-label" className="text-xs text-muted-foreground mb-1 block">Kunde</span>
      <div
        className={cn(
          "flex items-center justify-between gap-3 rounded-lg border-[1.5px] px-3 py-2.5",
          isOther ? "border-primary bg-primary/[0.04]" : "border-foreground",
        )}
        aria-labelledby="invoice-customer-label"
      >
        <div className="min-w-0">
          <div className="font-semibold truncate">
            {value?.name ?? "Ingen kunde"}
            {isOther && (
              <span className="ml-2 align-[1px] text-[11px] font-semibold text-primary bg-primary/10 rounded px-1.5 py-px">
                annen kunde
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            {value && !isOther && "Hovedkunde · "}
            {value?.organization_number && `org.nr ${value.organization_number} · `}
            {value?.poweroffice_customer_id != null ? `PowerOffice-kunde ${value.poweroffice_customer_id}` : "ikke koblet til PowerOffice"}
          </div>
        </div>
        {lockedReason ? (
          <span className="text-xs text-muted-foreground text-right">{lockedReason}</span>
        ) : (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" size="sm">Bytt kunde</Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[360px] p-0">
              <div className="p-2">
                <Input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Søk kundenavn eller org.nr"
                  aria-label="Søk etter kunde"
                />
              </div>
              <div role="listbox" aria-label="Velg kunde" className="max-h-80 overflow-y-auto pb-1">
                {groups.main && group("Hovedkunde", option(groups.main, "hovedkunde"))}
                {groups.used.length > 0 &&
                  group(
                    "Brukt på prosjektet",
                    groups.used.map((u) => option(u.customer, `${u.invoiceCount} ${u.invoiceCount === 1 ? "faktura" : "fakturaer"}`)),
                  )}
                {groups.matches.length > 0 &&
                  group(
                    "Alle kunder",
                    groups.matches.map((c) => option(c, `PowerOffice ${c.poweroffice_customer_id}`)),
                  )}
                {query.trim() && groups.matches.length === 0 && groups.used.length === 0 && (
                  <p className="px-3 py-2 text-sm text-muted-foreground">Ingen kunder matcher «{query.trim()}».</p>
                )}
                {!query.trim() && (
                  <p className="px-3 py-2 text-xs text-muted-foreground">Skriv for å søke i alle kunder.</p>
                )}
                <div className="border-t mt-1 pt-1">
                  <NewCustomerDialog
                    onCustomerCreated={(id) => {
                      onChange(id);
                      setOpen(false);
                      setQuery("");
                    }}
                    trigger={
                      <button
                        type="button"
                        className="flex w-full items-center gap-1.5 px-3 py-2 text-sm font-medium text-primary hover:underline"
                      >
                        <SrIcon name="plus" className="w-3.5 h-3.5" />
                        Ny kunde …
                      </button>
                    }
                  />
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
}
